import type { UserDto } from "@/api/types";
import type { GroupIconType } from "@/components/GroupIcon/GroupIcon";
import { GroupIcon } from "@/components/GroupIcon/GroupIcon";
import { AppHeader } from "@/components/Phone/AppCommon";
import { PhoneInner } from "@/components/Phone/PhoneInner";
import { UserAvatar } from "@/components/UserAvatar/UserAvatar";
import { Inline } from "@/design-system/Inline";
import { useTranslation } from "@/translations";
import { RuntimeColorBadge } from "@/ui-adapters/RuntimeColorBadge";

export interface AppGroupDetailsViewProps {
  name: string;
  description: string;
  memberCount: number;
  image?: string;
  icon: GroupIconType;
  user: UserDto;
  projectTintColor?: string;
}

export function AppGroupDetailsView({
  name,
  description,
  icon,
  memberCount,
  image,
  user,
  projectTintColor,
}: AppGroupDetailsViewProps): React.ReactNode {
  const { t } = useTranslation();

  return (
    <PhoneInner withCog>
      <AppHeader
        bgImage={image}
        profileImage={
          <span className="flex size-full items-center justify-center">
            <GroupIcon size={32} icon={icon} />
          </span>
        }
      />
      <aside className="float-right flex justify-end pt-space-lg pr-space-sm">
        <div className="relative z-10 mt-space-sm">
          <RuntimeColorBadge color={projectTintColor || "#23175A"}>
            {t("model.interest-group.action.follow")}
          </RuntimeColorBadge>
        </div>
      </aside>
      <section className="relative bg-background-default px-space-sm py-space-md">
        <span className="mr-control-narrow mb-space-xs block text-body-emphasis break-all">{name || "-"}</span>
        <Inline gap="xs" align="center">
          <UserAvatar size="xs" image={user.avatar} />
          <span>{t("model.interest-group.member-count", { count: memberCount })}</span>
        </Inline>
        <p className="mt-space-md break-all whitespace-pre-line">{description}</p>
      </section>
    </PhoneInner>
  );
}
